import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChartBarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import Button from "../../components/common/Button";
import analystPhoto from "../../assets/rahul-das.png";

const SEBI_REG = "INH000023719";
const BSE_RAASB = "7041";

/* ── shared motion preset ─────────────────────────────────────────────── */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
} as const;

/* ── small building blocks ────────────────────────────────────────────── */
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary-400 mb-3">
    {children}
  </span>
);

const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: string;
  center?: boolean;
}> = ({ eyebrow, title, subtitle, center = true }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""} mb-12`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">{title}</h2>
    {subtitle && <p className="mt-4 text-lg text-dark-300 leading-relaxed">{subtitle}</p>}
  </div>
);

const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-semibold text-white">{children}</span>
);

/* framed image with ambient glow behind it */
const FramedImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div className="relative">
    <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-primary-600/10 blur-3xl" />
    <div className="relative overflow-hidden rounded-2xl border border-dark-700 bg-dark-900 shadow-2xl">
      <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dark-950/50 to-transparent" />
    </div>
  </div>
);

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: ShieldCheckIcon,
      title: "Transparency",
      description: "Complete transparency in our research process, our methods, and every recommendation we publish.",
    },
    {
      icon: ChartBarIcon,
      title: "Data-Driven",
      description: "Every call is backed by thorough fundamental and technical analysis — never by speculation.",
    },
    {
      icon: UserGroupIcon,
      title: "Client-Centric",
      description: "Your financial success is the priority. Research is tailored to your risk profile, not to hype.",
    },
    {
      icon: AcademicCapIcon,
      title: "Education",
      description: "We empower clients with the knowledge to make informed, independent investment decisions.",
    },
  ];

  const credentials = [
    `SEBI Registered Research Analyst (${SEBI_REG})`,
    "Expert in fundamental and technical analysis",
    "Focus on risk-to-reward optimization",
    "Committed to regulatory compliance and transparency",
  ];

  const contact = [
    { icon: EnvelopeIcon, label: "rahul@analyzerview.com", href: "mailto:rahul@analyzerview.com" },
    { icon: PhoneIcon, label: "+91 9382093168", href: "tel:+919382093168" },
    {
      icon: MapPinIcon,
      label: "Kukshimla, Raidogachia, Purbasthali, Purba Barddhaman, 713513",
      href: null,
    },
  ];

  return (
    <div className="min-h-screen bg-dark-950 text-white">
      {/* ════════════════════ HERO ════════════════════ */}
      <section className="relative overflow-hidden border-b border-dark-800">
        {/* ambient glow + grid */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-primary-600/15 blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 md:py-12 text-center">
          <motion.div {...fadeUp}>
            <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-tight">
              Independent equity research,
              <br className="hidden sm:block" />{" "}
              <span className="text-primary-400">built on data and discipline</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base md:text-lg text-dark-300 leading-relaxed">
              AnalyzerView is a SEBI-Registered Research Analyst firm providing transparent,
              data-backed equity research for investors across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════ MISSION ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div {...fadeUp}>
              <Eyebrow>Our mission</Eyebrow>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                We don't chase returns. We balance risk.
              </h2>

              <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-dark-300">
                <p>
                  Our mission is to{" "}
                  <Highlight>empower investors with transparent, research-driven market insights</Highlight>{" "}
                  that prioritise risk management and reward optimisation above all else.
                </p>
                <p>
                  Consistent investment success is achieved not through predictions, but through{" "}
                  <Highlight>data, discipline, and regulatory integrity</Highlight>. Every report and
                  recommendation we publish is rooted in facts, validated by data, and delivered in full
                  compliance with SEBI's Research Analyst Regulations.
                </p>
                <p>
                  We help clients make informed, confident decisions through a balanced risk-to-reward
                  framework — ensuring capital protection, strategic clarity, and long-term confidence.
                </p>
              </div>

              <figure className="mt-8 rounded-2xl border-l-2 border-primary-500 bg-dark-900/60 py-5 pl-6 pr-5">
                <blockquote className="text-lg font-medium italic text-white">
                  “At AnalyzerView, we don't chase returns — we balance risk, validate data, and build trust.”
                </blockquote>
              </figure>

              <Link to="/pricing" className="mt-8 inline-block">
                <Button size="lg" className="group">
                  Start your journey
                  <ArrowRightIcon
                    className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </Button>
              </Link>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
              <FramedImage
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Market analysis dashboard"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════ THE FIRM ════════════════════ */}
      <section className="border-y border-dark-800 bg-dark-900/30 px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div {...fadeUp} className="order-2 lg:order-1">
              <FramedImage
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Data analysis charts"
              />
            </motion.div>

            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <Eyebrow>The firm</Eyebrow>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Who we are</h2>

              <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-dark-300">
                <p>
                  AnalyzerView is a <Highlight>SEBI-Registered Research Analyst firm</Highlight> dedicated to
                  independent, transparent, and data-backed equity research for investors across India.
                </p>
                <p>
                  Founded by <Highlight>Rahul Das</Highlight>, the firm specialises in{" "}
                  <Highlight>fundamental and technical analysis</Highlight>, focusing on risk-to-reward
                  optimisation and research-based decision-making rather than speculation.
                </p>
                <p>
                  We serve retail investors, HNIs, and NRIs with well-researched reports, thematic studies,
                  and investor education — all developed in strict accordance with SEBI's regulatory
                  standards.
                </p>
                <p>
                  By combining{" "}
                  <Highlight>analytical precision, modern technology, and ethical research practices</Highlight>,
                  we are building a platform for investors who value transparency, compliance, and long-term
                  growth.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════ VALUES ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="What we stand for"
              title="Our core values"
              subtitle="These principles guide everything we do and every recommendation we make."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="group rounded-2xl border border-dark-800 bg-dark-900/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/50 hover:bg-dark-900"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/10 ring-1 ring-inset ring-primary-500/20 transition-colors group-hover:bg-primary-500/20">
                  <value.icon className="h-6 w-6 text-primary-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-lg font-semibold text-white">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-dark-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ FOUNDER ════════════════════ */}
      <section className="border-y border-dark-800 bg-dark-900/30 px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            {/* photo + contact */}
            <motion.div {...fadeUp} className="lg:col-span-5">
              <div className="relative">
                <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-primary-600/10 blur-3xl" />
                <div className="relative overflow-hidden rounded-2xl border border-dark-700 bg-dark-900">
                  <img
                    src={analystPhoto}
                    alt="Rahul Das, Founder of AnalyzerView"
                    className="aspect-[4/5] w-full object-cover object-top"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-dark-950 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <p className="text-lg font-semibold text-white">Rahul Das</p>
                    <p className="text-sm text-primary-400">Founder &amp; SEBI Registered Research Analyst</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-dark-800 bg-dark-900/60 p-6">
                <h4 className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-primary-400">
                  Contact
                </h4>
                <ul className="space-y-4">
                  {contact.map(({ icon: Icon, label, href }) => (
                    <li key={label} className="flex items-start gap-3">
                      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-400" aria-hidden="true" focusable="false" />
                      {href ? (
                        <a href={href} className="text-sm text-dark-300 transition-colors hover:text-white">
                          {label}
                        </a>
                      ) : (
                        <span className="text-sm leading-relaxed text-dark-300">{label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* bio */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-7 lg:pt-4"
            >
              <Eyebrow>Leadership</Eyebrow>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Meet our founder</h2>

              <p className="mt-6 text-base md:text-lg leading-relaxed text-dark-300">
                Rahul Das is the founder of AnalyzerView, a SEBI-Registered Research Analyst with extensive
                experience in equity research, market analysis, and risk management. He brings a disciplined,
                data-driven approach to investment research.
              </p>

              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {credentials.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 rounded-xl border border-dark-800 bg-dark-950/60 p-4"
                  >
                    <CheckCircleIcon
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-400"
                      aria-hidden="true"
                      focusable="false"
                    />
                    <span className="text-sm leading-relaxed text-dark-300">{item}</span>
                  </li>
                ))}
              </ul>

              <figure className="mt-8 rounded-2xl border-l-2 border-primary-500 bg-dark-900/60 py-5 pl-6 pr-5">
                <blockquote className="text-base md:text-lg italic leading-relaxed text-dark-200">
                  “My goal is to help investors make informed decisions through transparent, research-backed
                  insights that prioritise risk management and long-term value creation.”
                </blockquote>
                <figcaption className="mt-3 text-sm text-dark-400">— Rahul Das, Founder</figcaption>
              </figure>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════ COMPLIANCE ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <motion.div
          {...fadeUp}
          className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-dark-800 bg-dark-900/60 p-8 md:p-12"
        >
          <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-96 -translate-x-1/2 rounded-full bg-primary-600/10 blur-[100px]" />

          <div className="relative flex flex-col items-center gap-6 text-center md:flex-row md:items-start md:gap-8 md:text-left">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-500/10 ring-1 ring-inset ring-primary-500/20">
              <ShieldCheckIcon className="h-8 w-8 text-primary-400" aria-hidden="true" focusable="false" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">SEBI registered &amp; compliant</h2>
              <p className="mt-4 text-base leading-relaxed text-dark-300">
                We are a SEBI registered research analyst (Registration No. {SEBI_REG}, BSE RAASB Enlistment
                No. {BSE_RAASB}) and comply with all regulatory requirements under the SEBI (Research Analysts)
                Regulations, 2014. Our research process follows strict guidelines to ensure transparency and
                investor protection.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="border-t border-dark-800 px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <motion.div {...fadeUp} className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Ready to start your investment journey?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-dark-300">
            Join investors who trust AnalyzerView for transparent, research-backed insights.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/register">
              <Button size="lg" className="group w-full sm:w-auto">
                Get started
                <ArrowRightIcon
                  className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Contact us
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
